"use client";

import { CircleAlert, CircleCheck, Info, LoaderCircle, TriangleAlert } from "lucide-react";
import { Toaster as Sonner, type ToasterProps } from "sonner";

/**
 * Toaster — PROJECT-OWNED, not installed.
 *
 * Taken from the default shadcn registry (`sonner`) because the design system does not
 * ship a transient notice: capture fields 8.8, 12.25 and 22.12 are all on its "not
 * decided — do not invent silently" list. Per that list's own instruction, this is a
 * decision made deliberately on the system's behalf and stated out loud rather than
 * quietly picked. If the system later ships one, the installer overwrites this file at
 * this exact path and every import keeps working.
 *
 * Three things were changed from the registry version, and all three matter:
 *
 * 1. **`next-themes` is gone.** The stock component reads the theme from it, which is a
 *    Next.js dependency this project has no reason to carry. It is unnecessary here
 *    anyway: every colour below is a semantic token that already flips under `.dark`,
 *    so the toast follows the theme without anything reading it in JavaScript.
 * 2. **Re-dressed in the system's vocabulary.** The registry version ships `text-sm`,
 *    `rounded-lg` and `size-4` — anonymous scale steps this system bans. They are
 *    translated to the `text-support` role and the `rounded-pill` alias, and the visual
 *    language matches the placeholder it replaces, which was already reasoned out from
 *    these tokens.
 * 3. **It clears the keyboard and the home indicator.** iOS slides the keyboard OVER the
 *    web view, and a notice pinned to the viewport bottom lands underneath it — or under
 *    the tab bar, which is worse because it looks deliberate. The offset is the same
 *    calculation the rest of the app's bottom-anchored chrome uses.
 *
 * This is the PRIMITIVE only. Nothing may import `sonner` directly to raise a message —
 * that goes through the announcement seam, so one failure stays one message.
 */

/** Tab bar, then the home indicator, then the keyboard when it is up. */
const BOTTOM_OFFSET = "calc(5rem + var(--safe-bottom) + var(--keyboard-height))";

function Toaster(props: ToasterProps) {
  return (
    <Sonner
      position="bottom-center"
      offset={{ bottom: BOTTOM_OFFSET }}
      mobileOffset={{ bottom: BOTTOM_OFFSET }}
      icons={{
        success: <CircleCheck className="size-5" aria-hidden />,
        info: <Info className="size-5" aria-hidden />,
        warning: <TriangleAlert className="size-5" aria-hidden />,
        error: <CircleAlert className="size-5" aria-hidden />,
        loading: <LoaderCircle className="size-5 animate-spin" aria-hidden />,
      }}
      toastOptions={{
        classNames: {
          toast: "rounded-pill bg-foreground text-background text-support gap-3 px-4 py-2.5",
          description: "text-background/70",
          actionButton: "rounded-pill bg-background text-foreground text-label px-3 py-1",
          cancelButton: "rounded-pill bg-background/20 text-background text-label px-3 py-1",
          icon: "shrink-0",
        },
      }}
      {...props}
    />
  );
}

export { Toaster };
